import { Box, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { FC } from "react";
import { usePermissionFilters } from "~/hooks/use-permission-filters";
import { $dayjs } from "~/libs/dayjs";
import { GetCurrentCompanyResults, useGetCurrentCompany } from "~/queries/useGetCurrentCompany";
import { GetFileByIdResults } from "~/queries/useGetFileById";
import { ReceiptPaymentPayment } from "~/types/__generated/gql/graphql";
import { $d } from "~/utils/currency-display";
import { $toOrderDisplay } from "~/utils/multi-currency";
import { DEFAULT_CURRENCY_CODE } from "~served/configs";
import { bigMath, calculateFile, DATESTRING_FORMAT } from "~served/utils";
import { FileInputLike, FileProductInputLike, FileProductOptionInputLike } from "~served/utils/calculations/types";
export interface PaymentRecordProps {
  key: string;
  createdAt: string;
  payment: ReceiptPaymentPayment;
  file: GetFileByIdResults;
  updatePaymentReceivedDate?: (val) => void;
  updatePaymentType?: (val: string) => void;
  updatePaymentAmount?: (val: number) => void;
}
interface PaymentRecordMultiCurrencyProps {
  key: string;
  createdAt: string;
  company: GetCurrentCompanyResults;
  payment: ReceiptPaymentPayment;
  file: GetFileByIdResults;
  updatePaymentReceivedDate?: (val: Date) => void;
  updatePaymentType?: (val: string) => void;
  updatePaymentAmount?: (val: number) => void;
}
interface PaymentRecordNonMultiCurrencyProps {
  key: string;
  createdAt: string;
  company: GetCurrentCompanyResults;
  payment: ReceiptPaymentPayment;
  updatePaymentReceivedDate?: (val: Date) => void;
  updatePaymentType?: (val: string) => void;
  updatePaymentAmount?: (val: number) => void;
}
export const PaymentRecord: FC<PaymentRecordProps> = ({
  key,
  createdAt,
  payment,
  file,
  updatePaymentReceivedDate,
  updatePaymentType,
  updatePaymentAmount
}) => {
  const {
    data: company
  } = useGetCurrentCompany();
  return company?.isMultiCurrencyEnabled ? <PaymentRecordMultiCurrency key={key} createdAt={createdAt} company={company} payment={payment} file={file} updatePaymentReceivedDate={updatePaymentReceivedDate} updatePaymentType={updatePaymentType} updatePaymentAmount={updatePaymentAmount} /> : <PaymentRecordNonMultiCurrency key={key} createdAt={createdAt} company={company!} payment={payment} updatePaymentReceivedDate={updatePaymentReceivedDate} updatePaymentType={updatePaymentType} updatePaymentAmount={updatePaymentAmount} />;
};
const PaymentRecordMultiCurrency: FC<PaymentRecordMultiCurrencyProps> = ({
  key,
  createdAt,
  company,
  payment,
  file,
  updatePaymentReceivedDate,
  updatePaymentType,
  updatePaymentAmount
}) => {
  const permissions = usePermissionFilters();
  const display = $toOrderDisplay(payment.multiCurrencyConfig, file.multiCurrencyConfig);
  return <Stack key={`payment-recrod-multi-currency-${key}`} sx={{
    m: 2,
    p: 2,
    width: "100%",
    overflowX: "auto"
  }} direction="row" spacing={2} data-sentry-element="Stack" data-sentry-component="PaymentRecordMultiCurrency" data-sentry-source-file="payment-record.tsx">
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<TextField disabled fullWidth label="Created At" value={$dayjs(createdAt).format(DATESTRING_FORMAT)} data-sentry-element="TextField" data-sentry-source-file="payment-record.tsx" />
			</Box>
			<Box sx={{
      width: 220,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<DateTimePicker sx={{
        width: "100%"
      }} label="Bank Received" disabled={payment.voidedAmount && payment.voidedAmount >= 0 ? true : false} value={payment.bank_received_date ? new Date(payment.bank_received_date) : null} onChange={value => {
        if (!value || !updatePaymentReceivedDate) return;
        void updatePaymentReceivedDate(value);
      }} data-sentry-element="DateTimePicker" data-sentry-source-file="payment-record.tsx" />
			</Box>
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<FormControl fullWidth sx={{
        flex: 1
      }} disabled={!permissions.EDIT_PAYMENT_TYPE || payment.voidedAmount && payment.voidedAmount >= 0 ? true : false} data-sentry-element="FormControl" data-sentry-source-file="payment-record.tsx">
					<InputLabel id="demo-simple-select-label" data-sentry-element="InputLabel" data-sentry-source-file="payment-record.tsx">Payment Type</InputLabel>
					<Select labelId="payment-type-select-label" id="payment-type-select" value={payment.payment_type} label="Payment Type" onChange={e => {
          if (!e.target.value || !updatePaymentType) return;
          void updatePaymentType(e.target.value);
        }} data-sentry-element="Select" data-sentry-source-file="payment-record.tsx">
						{company?.paymentTypes.map((pt, pi) => <MenuItem key={`sel-${pi}-${pt._id}`} value={pt._id}>
								{pt.name}
							</MenuItem>)}
					</Select>
				</FormControl>
			</Box>
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<TextField disabled={!permissions.EDIT_PAYMENT_AMOUNT || payment.voidedAmount && payment.voidedAmount >= 0 ? true : false} type="number" fullWidth sx={{
        flex: 1
      }} label="Payment Amount" defaultValue={payment.amount || 0} onBlur={e => {
        if (!e.target.value || !updatePaymentAmount) return;
        void updatePaymentAmount(Number(e.target.value));
      }} data-sentry-element="TextField" data-sentry-source-file="payment-record.tsx" />
			</Box>
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<TextField disabled fullWidth sx={{
        flex: 1
      }} label="Payment Currency" value={payment.multiCurrencyConfig?.selectedCurrency ?? DEFAULT_CURRENCY_CODE} data-sentry-element="TextField" data-sentry-source-file="payment-record.tsx" />
			</Box>
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<TextField disabled fullWidth sx={{
        flex: 1
      }} label="Exchange Rate" value={display.label} data-sentry-element="TextField" data-sentry-source-file="payment-record.tsx" />
			</Box>
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<TextField disabled fullWidth sx={{
        flex: 1
      }} label="Order Amount" value={$d(calculateFile(file as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).grossAmount ?? 0, file.multiCurrencyConfig?.selectedCurrency)} data-sentry-element="TextField" data-sentry-source-file="payment-record.tsx" />
			</Box>
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<TextField disabled fullWidth sx={{
        flex: 1
      }} label="Order Currency" value={display.primaryCurrencyCode} data-sentry-element="TextField" data-sentry-source-file="payment-record.tsx" />
			</Box>
			<Box sx={{
      width: 200,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<TextField disabled fullWidth sx={{
        flex: 1
      }} label="Pay Amt in Order Curr" value={$d(bigMath.mul(payment.amount, display.rate), file.multiCurrencyConfig?.selectedCurrency)} data-sentry-element="TextField" data-sentry-source-file="payment-record.tsx" />
			</Box>
		</Stack>;
};
const PaymentRecordNonMultiCurrency: FC<PaymentRecordNonMultiCurrencyProps> = ({
  key,
  createdAt,
  company,
  payment,
  updatePaymentReceivedDate,
  updatePaymentType,
  updatePaymentAmount
}) => {
  const permissions = usePermissionFilters();
  return <Stack key={`payment-recrod-non-multi-currency-${key}`} sx={{
    m: 2,
    p: 2,
    width: "100%",
    overflowX: "auto"
  }} direction="row" spacing={2} data-sentry-element="Stack" data-sentry-component="PaymentRecordNonMultiCurrency" data-sentry-source-file="payment-record.tsx">
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<TextField disabled fullWidth label="Created At" value={$dayjs(createdAt).format(DATESTRING_FORMAT)} data-sentry-element="TextField" data-sentry-source-file="payment-record.tsx" />
			</Box>
			<Box sx={{
      width: 220,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<DateTimePicker sx={{
        width: "100%"
      }} label="Bank Received" disabled={!updatePaymentReceivedDate || payment.voidedAmount && payment.voidedAmount >= 0 ? true : false} value={payment.bank_received_date ? new Date(payment.bank_received_date) : null} onChange={value => {
        if (!value || !updatePaymentReceivedDate) return;
        void updatePaymentReceivedDate(value);
      }} data-sentry-element="DateTimePicker" data-sentry-source-file="payment-record.tsx" />
			</Box>
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<FormControl fullWidth sx={{
        flex: 1
      }} disabled={!permissions.EDIT_PAYMENT_TYPE || !updatePaymentType || payment.voidedAmount && payment.voidedAmount >= 0 ? true : false} data-sentry-element="FormControl" data-sentry-source-file="payment-record.tsx">
					<InputLabel id="demo-simple-select-label" data-sentry-element="InputLabel" data-sentry-source-file="payment-record.tsx">Payment Type</InputLabel>
					<Select labelId="payment-type-select-label" id="payment-type-select" value={payment.payment_type} label="Payment Type" onChange={e => {
          if (!e.target.value || !updatePaymentType) return;
          void updatePaymentType(e.target.value);
        }} data-sentry-element="Select" data-sentry-source-file="payment-record.tsx">
						{company?.paymentTypes.map((pt, pi) => <MenuItem key={`sel-${pi}-${pt._id}`} value={pt._id}>
								{pt.name}
							</MenuItem>)}
					</Select>
				</FormControl>
			</Box>
			<Box sx={{
      width: 175,
      display: "flex",
      flexShrink: 0
    }} data-sentry-element="Box" data-sentry-source-file="payment-record.tsx">
				<TextField disabled={!permissions.EDIT_PAYMENT_AMOUNT || !updatePaymentAmount || payment.voidedAmount && payment.voidedAmount >= 0 ? true : false} type="number" fullWidth sx={{
        flex: 1
      }} label="Payment Amount" defaultValue={payment.amount || 0} onBlur={e => {
        if (!e.target.value || !updatePaymentAmount) return;
        void updatePaymentAmount(Number(e.target.value));
      }} data-sentry-element="TextField" data-sentry-source-file="payment-record.tsx" />
			</Box>
		</Stack>;
};